/**
 * ClientFilesGroup — TASK-818: one client's documents, as their own section.
 *
 * Replaces the flat account-wide lists. Within the group, deliverables and working documents
 * stay separate (TASK-741) — an export is a rendered artefact of a document at a version,
 * not another document.
 */

import React from 'react';
import { ChevronDown, ChevronRight, Download, UserRound } from 'lucide-react';

import { getExportDownloadHref, type ExportListItem } from '../../../services/api/exportApi';
import { NO_CLIENT, type ClientFileGroup } from '../../../utils/groupClientFiles';
import { fmtDate, formatIcon, initialsOf, reviewSummary } from './filePresentation';

interface ClientFilesGroupProps {
  group: ClientFileGroup;
  expanded: boolean;
  onToggle: () => void;
  onOpenDocument: (documentId: string) => void;
  deliverableLabel: (e: ExportListItem) => string;
}

const SubHeading: React.FC<{ children: React.ReactNode; count: number }> = ({
  children,
  count,
}) => (
  <div className="flex items-center gap-2 px-4 pb-1 pt-3">
    <h5 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </h5>
    <span className="text-[11px] text-gray-400">{count}</span>
  </div>
);

const MetaLine: React.FC<{ parts: (string | null | undefined)[] }> = ({ parts }) => (
  <p className="mt-0.5 truncate text-xs text-gray-500">
    {parts.filter(Boolean).join(' · ')}
  </p>
);

export const ClientFilesGroup: React.FC<ClientFilesGroupProps> = ({
  group,
  expanded,
  onToggle,
  onOpenDocument,
  deliverableLabel,
}) => {
  const unassigned = group.clientId === NO_CLIENT;

  return (
    <section
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-testid="client-file-group"
      data-client-id={group.clientId}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid="client-group-toggle"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
        )}
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
            unassigned ? 'bg-gray-100 text-gray-400' : 'bg-[#002B45] text-white'
          }`}
          aria-hidden
        >
          {unassigned ? <UserRound className="h-3.5 w-3.5" /> : initialsOf(group.clientName)}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm font-semibold ${
              unassigned ? 'text-gray-500' : 'text-gray-900'
            }`}
          >
            {group.clientName}
          </span>
          {unassigned && (
            // Not an error state — a brief deck generated from a meeting can legitimately
            // have no client. Saying so beats letting it look like a bug.
            <span className="block text-xs text-gray-400">
              Generated without a client reference
            </span>
          )}
        </span>
        <span className="shrink-0 text-xs text-gray-500">
          {group.total} file{group.total === 1 ? '' : 's'}
        </span>
        {group.lastActivityMs > 0 && (
          <span className="hidden shrink-0 text-xs text-gray-400 sm:inline">
            {fmtDate(new Date(group.lastActivityMs).toISOString())}
          </span>
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {group.deliverables.length > 0 && (
            <>
              <SubHeading count={group.deliverables.length}>Deliverables</SubHeading>
              <ul className="divide-y divide-gray-50">
                {group.deliverables.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
                    data-testid="deliverable-row"
                  >
                    <span className="shrink-0">{formatIcon(e.format ?? 'pptx')}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-800">
                        {deliverableLabel(e)}
                      </span>
                      <MetaLine
                        parts={[
                          (e.format ?? 'pptx').toUpperCase(),
                          e.version ? `v${e.version}` : null,
                          fmtDate(e.completed_at ?? e.created_at),
                        ]}
                      />
                    </span>
                    <a
                      href={getExportDownloadHref(e.id)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-[#002B45] hover:bg-gray-50"
                    >
                      <Download className="h-3 w-3" /> Download
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}

          {group.documents.length > 0 && (
            <>
              <SubHeading count={group.documents.length}>Working documents</SubHeading>
              <ul className="divide-y divide-gray-50">
                {group.documents.map((d) => (
                  <li
                    key={d.document_id}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
                    data-testid="working-document-row"
                  >
                    <span className="shrink-0">{formatIcon(d.format)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-900">
                          {d.title || `${d.format} document ${d.document_id.slice(0, 8)}`}
                        </span>
                        {d.source_stale && (
                          // TASK-745: staleness is visible in Files too, not only in the
                          // open workbench (the shell banner has the actions).
                          <span
                            data-testid="stale-source-badge"
                            title="Generated from a source document that has changed since — open to review or regenerate"
                            className="shrink-0 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800"
                          >
                            Source updated
                          </span>
                        )}
                      </span>
                      <MetaLine
                        parts={[
                          d.format.toUpperCase(),
                          `v${d.version}`,
                          reviewSummary(d),
                          fmtDate(d.updated_at),
                        ]}
                      />
                    </span>
                    <button
                      type="button"
                      onClick={() => onOpenDocument(d.document_id)}
                      className="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-[#002B45] hover:bg-gray-50"
                    >
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="h-2" />
        </div>
      )}
    </section>
  );
};

export default ClientFilesGroup;
